<template>
  <el-date-picker
    v-bind="$attrs"
    :size="sizeMap[props.size as keyof typeof sizeMap]"
    :class="[style.picker, `picker-${props.size}`, props.reverseDateIcon && style['reverse-date-icon']]"
    :popper-class="style.popper"
    :autocomplete="false"
  />
</template>

<script setup lang="ts">
const props = defineProps({
  size: {
    validator(value: string) {
      return ['max', 'large', 'default', 'small'].indexOf(value) > -1;
    }
  },
  reverseDateIcon: {
    type: Boolean,
    default: false
  }
});

const sizeMap: any = reactive({
  max: 'large',
  large: 'large',
  default: 'default',
  small: 'small',
  mini: 'small'
});
</script>

<style lang="scss" module="style">
.popper {
  --el-datepicker-active-color: var(--gray-gray10);
  --el-datepicker-hover-text-color: var(--text-text1);
  --el-datepicker-inrange-bg-color:var(--fill-fill1);
  --el-popper-border-radius: var(--radius-default);
  --el-border-radius-base: var(--radius-default);
  --el-datepicker-off-text-color:var(--text-text3);
  --el-bg-color-overlay: var(--base-base6);
  :global {
    .available {
      &:hover .el-date-table-cell__text {
        background-color: var(--fill-fill1);
        color: var(--text-text1);
      }
    }
    .el-date-table td.today .el-date-table-cell__text{
      color: var(--text-text1);
      background-color: var(--fill-fill1);
    }
    .el-date-table td.in-range .el-date-table-cell{
      background-color:var(--fill-fill2);
      color: var(--text-text3);
    }
    .el-date-table td .el-date-table-cell .el-date-table-cell__text {
      font-size: 14px;
      font-weight: 500;
      height: 32px;
      width: 32px;
      line-height: 32px;
      border-radius: 4px;
    }

    .el-date-table td.current:not(.disabled) .el-date-table-cell__text, .el-date-table td.end-date .el-date-table-cell__text, .el-date-table td.start-date .el-date-table-cell__text {
      color: var(--text-text6);
      background-color: var(--gray-gray10);

    }

    .el-date-table td{
      height: 40px;
      padding: 4px 0;
    }

    .el-date-table td .el-date-table-cell {
      padding: 0;
      height: 32px;
    }
  }
}

.picker {
  --el-text-color-regular: var(--text-text1);
  --el-input-border-color: var(--gray-gray2);
  --el-input-bg-color: var(--gray-gray0):none;
  --el-input-hover-border-color: var(--gray-gray10);
  --el-input-focus-border-color: var(--gray-gray10);

  &:global {
    &.el-range-editor.el-input__wrapper {
      height: var(--el-input-height)
    }
    &.el-date-editor--daterange{
      --el-date-editor-width: 100%;
    }
  }

  :global {
    .el-input__wrapper{
      display: flex;
    }
    .el-input__inner::placeholder{
      color: var(--text-text4);
    }
    .el-input__icon {
      font-size: var(--el-datepicker-icon-size) ;
      font-weight: var(--el-datepicker-font-weight);
      color: var(--text-text4);
    }

    .el-input__icon:first-child {
      margin-inline-end:8px
    }

    .el-range-input,
    .el-input__inner {
      font-size: var(--el-datepicker-font-size);
      font-weight: var(--el-datepicker-font-weight);
      &::placeholder {
        color: var(--text-text4);
      }
    }
    .suffix-content {
      display: flex;
      align-items: center;
      height: 100%;
      color: var(--text-text4);
      font-size: var(--el-datepicker-font-size);
    }

    .el-input__suffix {
      display: flex;
      align-items: center;
    }
  }
}

:global {
  .picker {

    &-max {
      --el-input-height: var(--height-max);
      --el-datepicker-font-size: var(--font-size-max);
      --el-datepicker-font-weight: var(--font-weight-max);
      --el-input-border-radius: var(--radius-max);
      --el-datepicker-icon-size: calc(var(--font-size-max) + 4px);
    }

    &-large {
      --el-input-height: var(--height-large);
      --el-datepicker-font-size: var(--font-size-large);
      --el-datepicker-font-weight: var(--font-weight-large);
      --el-input-border-radius: var(--radius-large);
      --el-datepicker-icon-size: calc(var(--font-size-max) + 2px);
    }

    &-default {
      --el-input-height: var(--height-default);
      --el-datepicker-font-size: var(--font-size-default);
      --el-datepicker-font-weight: var(--font-weight-default);
      --el-input-border-radius: var(--radius-default);
      --el-datepicker-icon-size: calc(var(--font-size-default) + 2px);
    }

    &-small {
      --el-input-height: var(--height-small);
      --el-datepicker-font-size: var(--font-size-small);
      --el-datepicker-font-weight: var(--font-weight-small);
      --el-input-border-radius: var(--radius-small);
      --el-datepicker-icon-size:var(--font-size-small);
    }

    &-mini {
      --el-input-height: var(--height-mini);
      --el-datepicker-font-size: var(--font-size-mini);
      --el-datepicker-font-weight: var(--font-weight-mini);
      --el-input-border-radius: var(--radius-mini);
      --el-datepicker-icon-size:var(--font-size-mini);
    }
  }
}
// 倒置图标
.reverse-date-icon {
  :global {
    & .el-input__wrapper {
      .el-input__prefix {
        order: 3;

        .el-input__icon {
          font-size: var(--el-datepicker-icon-size);
          font-weight: var(--el-datepicker-font-weight);
          color: var(--text-text2);
          margin-inline-end: 0px;
        }
      }

      .el-input__suffix {
        order: 1;
        display: none;
      }

      .el-input__inner {
        order: 2;
      }
    }
  }
}
</style>
